import type { Platform } from '@discovr/types';

/** Wording for the fetched view stats, which are measured over a different
 *  kind of content per platform: Instagram reels, YouTube uploads. */
export interface ContentMetricsCopy {
  /** Caption when the fetch found nothing with a view count. */
  noContent: string;
  /** "Based on N …" in the Avg Views breakdown. */
  sampleNoun: (count: number) => string;
  /** Badge when fewer than three items carried views. */
  thinSampleBadge: string;
  thinSampleWarning: string;
  avgViewsInfoAdmin: string;
  avgViewsInfo: string;
  /** What the sheet-rules popover says the stats are read from. */
  recentContent: string;
}

const plural = (one: string, many: string) => (count: number) => (count === 1 ? one : many);

const INSTAGRAM_COPY: ContentMetricsCopy = {
  noContent: 'No reels',
  sampleNoun: plural('reel', 'reels'),
  thinSampleBadge: 'photo-led',
  thinSampleWarning: 'Photo-led profile — too few reels to read views reliably',
  avgViewsInfoAdmin:
    'The sheet’s average, or for fetched profiles the median over the last 10 reels. Fetched rows show the mean beneath — a few viral reels drag it up, so the median is the truer read.',
  avgViewsInfo: 'Average views — from the sheet, or measured over the last 10 reels',
  recentContent: 'recent reels',
};

const YOUTUBE_COPY: ContentMetricsCopy = {
  noContent: 'No videos',
  sampleNoun: plural('video', 'videos'),
  thinSampleBadge: 'few videos',
  thinSampleWarning: 'Too few videos with views to read them reliably',
  avgViewsInfoAdmin:
    'The sheet’s average, or for fetched channels the median over the last 10 uploads with views, Shorts included. Fetched rows show the mean beneath: a few viral videos drag it up, so the median is the truer read.',
  avgViewsInfo: 'Average views, from the sheet or measured over the last 10 uploads (Shorts included)',
  recentContent: 'latest uploads',
};

/** Wording for a platform whose stats are not fetched, or not yet known. */
const NEUTRAL_COPY: ContentMetricsCopy = {
  noContent: 'No content',
  sampleNoun: plural('item', 'items'),
  thinSampleBadge: 'thin sample',
  thinSampleWarning: 'Too few items with views to read them reliably',
  avgViewsInfoAdmin:
    'The sheet’s average, or for fetched profiles the median over the latest items with views. Fetched rows show the mean beneath: a few viral items drag it up, so the median is the truer read.',
  avgViewsInfo: 'Average views, from the sheet or measured over the latest items',
  recentContent: 'recent content',
};

/** Platforms whose avg views / engagement rate the BE's metrics job fetches
 *  (its platformFetchesMetrics). A platform added to Platform opts in here,
 *  never by having copy. */
const METRICS_FETCH_PLATFORMS: ReadonlySet<Platform> = new Set<Platform>(['instagram', 'youtube']);

const COPY_BY_PLATFORM: Partial<Record<Platform, ContentMetricsCopy>> = {
  instagram: INSTAGRAM_COPY,
  youtube: YOUTUBE_COPY,
};

/** The campaign platform when its metrics are fetched; null otherwise. Exact
 *  trim + lowercase, like the BE: resolvePlatform's fuzzy matching would
 *  accept "YouTube Shorts" or "instagram_reel", which the BE does not fetch. */
function metricsPlatform(platform: string | undefined): Platform | null {
  const key = (platform ?? '').trim().toLowerCase();
  for (const p of METRICS_FETCH_PLATFORMS) if (p === key) return p;
  return null;
}

export function platformFetchesMetrics(platform: string | undefined): boolean {
  return metricsPlatform(platform) !== null;
}

/** Neutral wording until the campaign platform is known, or when its stats
 *  are not fetched. */
export function contentMetricsCopy(platform: string | undefined): ContentMetricsCopy {
  const resolved = metricsPlatform(platform);
  return (resolved && COPY_BY_PLATFORM[resolved]) || NEUTRAL_COPY;
}
